/**
 * Copy for the Spaces surface — the space table, the two creation panels, and
 * the members panel (SPEC §4, FIL-9, CON-5..9, CON-11, CON-14, CON-22, CON-23).
 *
 * Sentence case throughout — `Eyebrow` and `Badge` apply the uppercase and the
 * wide tracking themselves, so writing it uppercase here would double it.
 *
 * `dateLocale` and the date format live here because formatting is a
 * presentation choice that changes with language, and this file is the seam
 * where that will be dealt with.
 *
 * **One copy file for four components, because they are one surface.**
 * `connectPanel.copy.ts` was a second file for the one panel that used to hang
 * off this table; that panel is now two panels and a third beside them, all
 * reached from a row of this table, and splitting four files four ways would put
 * "Add an agency" in one file and the roster it adds to in another.
 */
export const SPACE_LIST_COPY = {
  caption: 'Spaces your agency belongs to',

  /**
   * Two headers were renamed for what they are read as rather than for what
   * they are.
   *
   * **"Clearance" → "CJI status".** Standing alone in a law-enforcement
   * product, "Clearance" reads as a *person's* security clearance. It is not:
   * CON-4 makes this an assertion about the counterpart agency, display-only,
   * enforcing nothing. Naming CJI in the header is what stops the misreading.
   * It is deliberately not "CJI sharing", which would imply a control.
   * (CON-4 has since withdrawn the badge outright; the reasoning is kept
   * because it is the precedent the next rename will be argued against.)
   *
   * **"Direction" → "Sharing" → gone.** The rename was sound — Direction is
   * accurate and abstract, and it deliberately was not "Permissions", because
   * §5.2 already owns that word for the per-user grant model on the Users
   * surface and the same word for two concepts on adjacent screens is worse
   * than an abstract one. The column went anyway, on product direction: CON-13's
   * `participation` is the reader's own capability, and this table is about the
   * rooms. It is stated inside each space, on `SpaceRoot`'s access badge, and
   * read from the same wire field by `SpaceAccessFieldset` when an admin grants
   * somebody access. Both argument and value survive; only the column is gone.
   *
   * **"Agency" → "Members", and the copy column stopped being called "Space".**
   * There were two columns headed "Space" — the room's name and FIL-9's copy
   * control — which is a table saying the same word twice about two different
   * things. The roster took the old counterpart column, and the control is
   * named for what it hands over.
   */
  columns: {
    space: 'Space',
    members: 'Members',
    status: 'Status',
    activity: 'Last activity',
    link: 'Link',
    actions: 'Actions',
  },

  /**
   * This agency's own membership status (CON-23). A space has no single one:
   * a room can hold three `Active` members, two `Invited` and one `Removed`.
   */
  status: {
    Invited: 'Invited',
    Active: 'Active',
    Removed: 'Removed',
  },

  /** CON-6: the handshake is waiting on this agency's admins. */
  awaitingAcceptance: 'Awaiting your acceptance',

  /** A space that has never been used. */
  noActivity: 'No activity',

  /**
   * The roster cell — who is in the room, in the row that names it.
   *
   * **Bounded, because the cell is a summary and the panel is the report.**
   * A-10 sizes a space at about twenty-five agencies, and twenty-five names in a
   * table cell is a paragraph in a column. The first few are named and the rest
   * are counted, which is `AGENCY_GRID_COPY.moreMembers`' rule one surface over —
   * duplicated rather than imported, on this workspace's standing convention for
   * near-identical strings across two surfaces.
   */
  roster: {
    /** Appended after the named members. Never rendered at zero. */
    more: (count: number) => `+${String(count)} more`,
    /** Every member of this room has been removed except the reader's own. */
    none: 'No other agencies',
  },

  /**
   * Admin-only row actions (CON-6, CON-14).
   *
   * **Leaving and removing left this table for the members panel**, and the
   * table's own menu went with them. One route carries both of CON-14's verbs
   * and the target is what tells them apart (ADR-0071) — so the control has to
   * sit beside the agency it names, which is a roster rather than a row. What is
   * left here is the accept a member owes (CON-6), the way into the roster, and
   * FIL-9's address.
   */
  actions: {
    /**
     * FIL-9 / CON-15, moved here from the space root's own header (product
     * direction) — the root-only URL an agency copies and sends **outside**
     * the app. There are no deep links to a folder or a file, and opening it
     * still requires signing in.
     *
     * Replaces "Open space": browsing into the space is Files' job now
     * (ADR-0032), and what this table offers instead is the address to hand a
     * counterpart.
     */
    copyLink: 'Copy link',
    copyLinkDone: 'Link copied',
    copyLinkFailed: 'Could not copy. Use the address bar.',
    accept: 'Accept',
    accepting: 'Accepting…',
    /**
     * The way into the roster — an item inside the row's "⋯" now, alongside
     * Accept and Delete this space. It used to be a labelled button of its
     * own, kept out of the overflow on the reasoning that the acts behind it
     * were this surface's whole point and hiding the door to them was how the
     * old table hid Sever — the opposite kind of control. A row offering
     * exactly one control regardless of state won that argument instead; the
     * menu's own accessible name (`moreFor`) is what still names the room.
     */
    members: 'Members',
    /**
     * Owner-only, and an act on the room rather than on anybody in it — which
     * is why it opens a drawer from here rather than living in the members
     * panel beside the acts that each name an agency (**ADR-0102**).
     */
    rename: 'Rename this folder',
    expiryWindow: 'How long files are kept',
    /**
     * CON-26's overflow, named per room — a table of nine needs nine distinct
     * accessible names, on `members.moreFor`'s precedent one surface over.
     */
    moreFor: (spaceName: string) => `More actions for ${spaceName}`,
    /** The trigger's visible glyph — `aria-hidden`; `moreFor` carries the name. */
    moreGlyph: '⋯',
    failed: 'That did not work. Reload and try again.',
  },

  states: {
    loading: 'Loading your shared spaces…',
    /** PERM-2: a member with no grants sees an empty Files surface. */
    empty: 'No shared spaces yet. An admin at your agency grants access to a space.',
    error: 'Your shared spaces could not be loaded.',
    forbidden: 'You do not have access to any shared spaces.',
    retry: 'Try again',
  },

  /**
   * **Opening the room — its own act, and it asks for one thing** (CON-22,
   * ADR-0071).
   *
   * `POST /connect` took an address and produced a room named after whatever the
   * client could guess the domain resolved to, because the room had no existence
   * apart from the pair that made it. A space has a name somebody chose, so the
   * form asks for the name and nothing else, and the room exists with one member
   * — the agency that made it — before any agency is invited into it.
   *
   * CON-7a's subscription gate is on this request, which is why the failure here
   * shows the API's own message: it names what the agency can still do.
   */
  createSpace: {
    open: 'Create a folder',
    title: 'Create a folder',
    close: 'Close',
    intro:
      'A folder is a room your agency owns. Give it a name, then add the agencies that work in it.',
    label: 'Folder name',
    hint: 'What the room is for — for example, First Appearances.',
    submit: 'Create folder',
    submitting: 'Creating…',
    cancel: 'Cancel',
    /*
      **There is no success line, and its removal is the record of a change.**
      It read `“{name}” is open. Add an agency to it from its members panel.` —
      naming the next act, because an empty room is not obviously a finished
      job. The panel performs that act now: creating hands over to the new
      room's members panel, so the sentence would be an instruction to do the
      thing the reader is already looking at.
    */
    errors: {
      nameRequired: 'Give the folder a name.',
      failed: 'The folder could not be created.',
    },
  },

  /**
   * Renaming a room (CON-22, CON-25, **ADR-0102**).
   *
   * **Says the two things a reader would otherwise have to find out by
   * trying.** Every member agency sees the new name at once — a name is not a
   * per-agency label — and nothing an agency has already saved breaks, because
   * no address is built from it (SYS-3). The second clause exists because the
   * first one is alarming without it.
   *
   * `errors.failed` is the fallback only. The API's own refusal is what
   * renders when there is one, and the one worth knowing about is the 403 an
   * admin of a fellow member gets: the room is theirs to work in and its name
   * is not theirs to change.
   */
  renameSpace: {
    title: 'Rename this folder',
    close: 'Close',
    intro:
      'Every agency in this folder sees the new name straight away. Links, subfolders and files are unaffected — nothing anyone has saved will stop working.',
    label: 'Folder name',
    hint: 'What the room is for — for example, First Appearances.',
    submit: 'Rename folder',
    submitting: 'Renaming…',
    cancel: 'Cancel',
    errors: {
      nameRequired: 'Give the folder a name.',
      failed: 'The folder could not be renamed.',
    },
  },

  /**
   * **How long files are kept in a room** (RET-12). Owner-only, reached from the
   * row's "⋯" like Rename. The voice keeps to "files are moved to the recycle
   * bin" rather than "deleted", because that is what happens — they are
   * restorable for 30 days, and the sweep is nightly, not immediate.
   */
  expiryWindow: {
    title: (spaceName: string) => `How long files are kept in ${spaceName}`,
    close: 'Close',
    done: 'Done',
    intro:
      'Files nobody has downloaded for this many days are moved to the recycle bin, where they can still be restored for 30 days. Downloading a file starts its count again. The default is 180 days; you can shorten it, not extend it.',
    label: 'Days files are kept',
    hint: 'Between 1 and 180. This applies to every agency’s files in this folder, including your own.',
    submit: 'Save',
    submitting: 'Saving…',
    cancel: 'Cancel',
    result: {
      set: (days: number) => `Files here are now kept for ${String(days)} days.`,
      due: (days: number, count: number) =>
        `Files here are now kept for ${String(days)} days. ${String(count)} ${
          count === 1 ? 'file is' : 'files are'
        } already past that and will move to the recycle bin at the next daily sweep.`,
    },
    errors: {
      range: 'Choose a whole number of days between 1 and 180.',
      failed: 'The keep-for setting could not be changed.',
    },
  },

  /**
   * **Inviting an agency into a room that already exists** (CON-6..CON-9).
   *
   * `POST /connect`'s body, unchanged, addressed to a space. Which of the two
   * branches runs is the API's decision, made on the address's domain (SPEC
   * §4.2); this panel only reports which one did, because they end in different
   * places — branch A waits on an admin who already exists, branch B waits on a
   * stranger to sign in for the first time.
   *
   * The voice here is doing real work. An admin is about to hand another
   * organisation a view of their evidence, and the clearance line is the only
   * warning they get that the checkbox is their assertion rather than a fact the
   * system checked.
   */
  addAgency: {
    open: 'Add an agency',
    title: (spaceName: string) => `Add an agency to ${spaceName}`,
    close: 'Close',
    intro: 'Choose an agency your agency is connected to.',
    fields: {
      /*
        **A picker, not an address** (CON-5, ADR-0095). The field asked for an
        email because CON-7's branch B minted an agency out of one; agencies
        come from the operator now, so the admin chooses from the list Blue
        Partner curates for their agency (CON-27, CON-28).

        `agencyNone` is the honest empty state and names the remedy: an agency
        with no connections cannot add anybody, and only Blue Partner can
        change that.
      */
      agency: 'Agency',
      agencyPlaceholder: 'Choose an agency…',
      agencyOption: (name: string, domain: string) => `${name} · ${domain}`,
      agencyHint: 'Agencies your agency is connected to.',
      agencyNone: 'No connected agencies. Contact Blue Partner to be connected to one.',
      /** CON-4: display-only. It informs judgment; it enforces nothing. */
      cjiCleared: 'I confirm this agency is CJI cleared',
      cjiClearedHint:
        'Shown wherever this agency is listed. Blue Partner verifies clearance separately when an agency subscribes.',
      /**
       * CON-24, chosen at invite time — the second of the two facts a member is
       * added with (CON-23), beside the attestation above.
       *
       * **Second person here and third person on the roster**, which is not an
       * inconsistency: this field is a question put to the admin about an agency
       * that is not yet in the room, and `members.scopes` is a statement about
       * an agency that is. The reader is different and so is the tense.
       *
       * **The hint says it can be changed**, because the honest thing about this
       * control is that it is not a commitment: the owner sets a scope at any
       * time on a room already in use (CON-25), and an admin who thinks this is
       * their one chance will pick `all` to be safe — which is the opposite of
       * what CON-24 exists for.
       */
      visibilityScope: 'What this agency will see',
      visibilityScopeHint: 'You can change this at any time from the members list.',
    },
    /**
     * The two options, worded from the invited agency's side, and **not** shared
     * with `members.scopes`.
     *
     * They differ by one word — "will" — and that word is the whole difference
     * between a choice and a fact. Sharing one string would have the roster
     * describing an arrangement in the future tense, which is exactly the
     * misreading `visibilityScopeHint` above exists to prevent.
     */
    scopes: {
      all: 'Every file in this space',
      own: 'Only the files it uploads',
    },
    submit: 'Add agency',
    submitting: 'Adding…',
    cancel: 'Cancel',
    /**
     * **One sentence where there were two** (ADR-0095). The pair told an admin
     * which of CON-7's branches had run — whether they were waiting on an
     * acceptance or on somebody signing in for the first time. There is one
     * act now, and one thing to wait for.
     */
    result: 'Agency added. It joins once an admin there accepts.',
    errors: {
      agencyRequired: 'Choose the agency you want to add.',
      /** CON-28: the operator curates the list, so a failed load names them. */
      connectionsFailed: 'Your connected agencies could not be loaded.',
      /** CON-8 as amended: the API names the agency and who can change the list. */
      failed: 'The agency could not be added.',
    },
  },

  /**
   * **The roster, and every act that changes it** (CON-11 as amended, CON-14,
   * CON-22, CON-23, EXIT-7).
   *
   * A member sees who is in the room with them — names, domains, types (CON-21)
   * and each membership's status. It stops there: no people, no grants, no other
   * spaces. CON-11 was crossed deliberately to publish this (ADR-0066).
   */
  members: {
    title: (spaceName: string) => `Members of ${spaceName}`,
    close: 'Close',
    /** Owner-only, and it opens `addAgency` above (CON-22, §5.3). */
    add: 'Add an agency',
    /**
     * Ending the room (CON-26). Owner-only, like `add` above, and offered from
     * the same drawer because both are acts on the room rather than in it.
     *
     * **The confirm names what survives, not only what goes.** An admin's real
     * question before pressing this is whether they are destroying evidence,
     * and the answer is no — CON-26 refuses while any file is unpurged, and the
     * audit history outlives the room by AUD-1. Saying so is what makes the act
     * safe to offer rather than merely permitted.
     *
     * **`conflict` is the 409 written out.** The API's own message names the
     * remedy, and this is the fallback for when it cannot be read — deliberately
     * the same two steps in the same order.
     */
    deleteSpace: {
      label: 'Delete this space',
      confirm: {
        title: 'Delete this space?',
        body: 'The room, its folders, and every agency’s membership of it are removed. This cannot be undone, and a space with the same name would be a different room.',
        keeps:
          'Nothing is destroyed: the audit history stays, and a space can only be deleted once every file in it has been purged.',
        submit: 'Delete the space',
        cancel: 'Keep it',
      },
      working: 'Deleting…',
      conflict: 'That space still holds files. Delete them and empty the recycle bin first.',
      failed: 'That space could not be deleted.',
    },
    /** The one agency that administers the room, and it does not transfer (CON-22). */
    owner: 'Owner',
    /** Per membership (CON-23), and shown only where it is not `Active`. */
    status: {
      Invited: 'Invited',
      Active: 'Active',
      Removed: 'Removed',
    },
    /**
     * CON-24's two values, named by what the agency **reads** (CON-24 as
     * amended, ADR-0067).
     *
     * Shown on every member's row to every member, which is the amendment: an
     * agency deciding whether to put evidence in a room needs to know who will
     * be reading it, and the reassuring reading is the common one — a police
     * agency learning that its neighbours are `own`-scope has learned that they
     * are not reading its booking sheets.
     *
     * **Four decisions, and each of them is the reason a shorter string was
     * rejected:**
     *
     * - **They are sentences about reading, because `all` and `own` are facts
     *   about a column and nobody is asking about the column.** The question a
     *   person opens this panel with is "does this agency see my files", so the
     *   answer has to be in those words. Neither wire value appears anywhere a
     *   reader can see it, which also leaves the request body free to be read in
     *   a network tab and this file free to be rewritten without touching it.
     * - **Third person, with the agency as the subject, so one string serves
     *   every row** — `termEnds`' rule, and it matters more here because the
     *   reader's own agency is on this list (CON-11 as amended). The drafting
     *   suggestion was "Only their own uploads", and "their" is exactly the word
     *   that breaks: on your own row it means you and on the row below it means
     *   somebody else, one line apart.
     * - **"here" rather than "in this space"**, because the drawer is titled
     *   with the space's name three inches above. Naming it again is furniture.
     * - **Nothing about what the agency *has* read.** Scope says what an agency
     *   *may* read and never what it did; that stays in the audit log where AUD-3
     *   puts it, and a roster line that implied otherwise would be the one
     *   inference CON-24's amendment is careful to rule out.
     */
    scopes: {
      all: 'Sees every file here',
      own: 'Sees only its own uploads',
    },
    /**
     * The owner's act, in the row overflow (§5.3 — one of the room's
     * owner-only rows).
     *
     * **One item rather than a picker, because two values make a change a
     * toggle.** The menu offers the scope the agency is *not* at, worded as the
     * act rather than as the state: a menu is a list of things you can do, and
     * `scopes` above is already the list of things that are true.
     *
     * `own` is worded as a limit and `all` is not, deliberately. Widening is
     * restoring the room's default — every space behaved this way before CON-24
     * existed — and narrowing is the decision somebody is making.
     */
    setScope: {
      all: (agencyName: string) => `Show ${agencyName} every file here`,
      own: (agencyName: string) => `Limit ${agencyName} to its own uploads`,
    },
    /**
     * The confirmation, outside the menu — this roster overflow's own choice
     * now rather than a workspace-wide rule (`components/README.md` has the
     * two that moved theirs inside instead), and the reason it applies to a
     * widening as much as to a narrowing is that both change what another
     * organisation can read.
     *
     * Both halves state the **outcome**, not the act, because that is the thing
     * an owner is being asked to check. It takes effect on the next read and is
     * not retroactive (CON-25): a widening reveals files that were always there
     * and a narrowing hides them again, and neither reaches what somebody has
     * already downloaded — which is why nothing here is worded as a warning.
     */
    confirmScope: {
      all: (agencyName: string) => `Confirm: ${agencyName} sees every file here`,
      own: (agencyName: string) => `Confirm: ${agencyName} sees only its own uploads`,
    },
    cancelScope: 'Leave it as it is',
    savingScope: 'Saving…',
    /**
     * EXIT-7 / A-8, one line per member agency that has a term.
     *
     * **Worded as a fact about that agency, not as an instruction to this
     * reader**, because the same string is rendered beside the reader's own
     * agency: "download what you need before" would be nonsense on your own row.
     * A date already past is shown, and reads correctly as written.
     */
    termEnds: (date: string) => `Subscription ends ${date}`,
    /**
     * **The two verbs of CON-14, said differently on purpose.** They are one
     * route with one target field (ADR-0071), and they are not one act: removing
     * ends somebody else's access to a room you own, leaving ends your own, and
     * an owner cannot refuse the second. A shared label would make the row that
     * names your own agency read as an administrative act against a third party.
     */
    remove: 'Remove from this space',
    removing: 'Removing…',
    confirmRemove: (agencyName: string) => `Confirm: ends ${agencyName}’s access`,
    cancelRemove: 'Keep them',
    leave: 'Leave this space',
    leaving: 'Leaving…',
    confirmLeave: 'Confirm: ends your agency’s access',
    cancelLeave: 'Stay',
    /** CON-6, on this agency's own membership. The same act the row offers. */
    accept: 'Accept the invitation',
    accepting: 'Accepting…',
    /**
     * `RowActionsMenu`'s trigger, named per member — a roster of nine needs nine
     * distinct accessible names, on `RestoreButton`'s precedent.
     */
    moreFor: (agencyName: string) => `More actions for ${agencyName}`,
    /** The trigger's visible glyph — `aria-hidden`, `moreFor` carries the accessible name. */
    moreGlyph: '⋯',
    failed: 'That did not work. Reload and try again.',
    /**
     * Every membership but the reader's own has ended. A room of one is a real
     * state — an owner whose invitations were all declined by silence, or whose
     * members have all left — and it is not an error.
     */
    empty: 'No other agencies in this space yet.',
    /**
     * CON-21's icon labels — the accessible name for the glyph beside an
     * agency's name. Duplicated from `AGENCY_GRID_COPY`, on this workspace's
     * standing convention: the two surfaces must not disagree, and a copy file
     * is never imported into another component.
     */
    agencyTypes: {
      LawEnforcement: 'Law Enforcement',
      Prosecution: 'Prosecution',
      Defense: 'Defense',
      Court: 'Court',
      ClerkOfCourt: 'Clerks of Court',
    },
  },

  dateLocale: 'en-US',
} as const;
